import { useState } from 'react';

export const NAMI_PANEL_DEFAULT_WIDTH = 420;
export const NAMI_PANEL_MIN_WIDTH = 360;
export const NAMI_PANEL_MAX_WIDTH = 640;
export const NAMI_PANEL_WIDTH_STORAGE_KEY = 'nami.panel.width';

export function clampNamiPanelWidth(width: number): number {
  if (!Number.isFinite(width)) return NAMI_PANEL_DEFAULT_WIDTH;
  return Math.min(
    NAMI_PANEL_MAX_WIDTH,
    Math.max(NAMI_PANEL_MIN_WIDTH, Math.round(width)),
  );
}

function readStoredWidth(): number {
  try {
    const raw = window.localStorage.getItem(NAMI_PANEL_WIDTH_STORAGE_KEY);
    if (raw === null) return NAMI_PANEL_DEFAULT_WIDTH;
    return clampNamiPanelWidth(Number(raw));
  } catch {
    // Private windows and blocked storage throw; the default is fine.
    return NAMI_PANEL_DEFAULT_WIDTH;
  }
}

function writeStoredWidth(width: number): void {
  try {
    window.localStorage.setItem(NAMI_PANEL_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Persistence is a convenience, never a requirement.
  }
}

type WidthUpdate = number | ((current: number) => number);

/**
 * The docked panel's width in px, clamped to a usable range and remembered per
 * browser. Storage failures are swallowed: the panel must work without them.
 */
export function useNamiPanelWidth(): [number, (update: WidthUpdate) => void] {
  const [width, setWidthState] = useState<number>(readStoredWidth);

  const setWidth = (update: WidthUpdate) => {
    setWidthState((current) => {
      const next = clampNamiPanelWidth(
        typeof update === 'function' ? update(current) : update,
      );
      if (next !== current) writeStoredWidth(next);
      return next;
    });
  };

  return [width, setWidth];
}
